import { AUFTRAG_API } from '@Constants/backendConstants';
import { useOrderPermissions, useOrderTotals } from '@Hooks/Orders';
import { useBadges } from '@Hooks/useBadges';
import { type ApiOrder, EnumOrderType, isBooked } from '@Model';
import {
    EditFrameMode,
    EditFrameShell,
    type IEditFrame,
    useGuiBinder,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useMemo } from 'react';
import {
    acceptsContainerChanges,
    acceptsNewContainerActions,
    siteCorrectionOf,
    useStateAtEditStart,
} from '../Containers';
import { orderActionItems, orderMenuItems } from './orderHeaderItems';
import { OrderCollectionModal, OrderOutputModals } from './OrderModals';
import { OrderSidePanel } from './OrderSidePanel';
import {
    OrderContainersSection,
    OrderDispositionSection,
    OrderMainSection,
    OrderPositionsSection,
    OrderRemarksSection,
} from './Sections';
import { useOrderActions } from './useOrderActions';
import { useOrderContainers } from './useOrderContainers';

interface IProps {
    frame: IEditFrame<ApiOrder>;
    embedded: boolean;
    staging: boolean;
    openOrder: (orderId: string) => void;
}

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function OrderFrameBody({ frame, embedded, staging, openOrder }: IProps) {
    const i18n = useTranslate('');

    const order = frame.data;
    const apiResourcePath = `${AUFTRAG_API}/orders`;
    const baseApiObjectPath = `${apiResourcePath}/${order.id}`;
    const canEdit = frame.mode !== EditFrameMode.View;

    const { forceReadOnly } = useOrderPermissions(order);
    const binder = useGuiBinder<ApiOrder>({
        data: order,
        meta: frame.meta,
        canEdit,
        requestUpdateState: frame.requestUpdateState,
        forceReadOnly,
    });

    const actions = useOrderActions(frame, staging);
    const containers = useOrderContainers(frame, embedded, openOrder);
    const canEditContainers =
        canEdit && !forceReadOnly('container_actions') && acceptsContainerChanges(order.process_state);
    const atEditStart = useStateAtEditStart(order, canEditContainers);
    const siteCorrection = useMemo(
        () => atEditStart && siteCorrectionOf(atEditStart.actions, order.container_actions),
        [atEditStart, order.container_actions],
    );
    const { totalGrossPrice, totalNetPrice } = useOrderTotals(order);
    const stateBadges = useBadges(order);

    const frameEvents = {
        ...frame.events,
        onDelete: actions.canDelete ? frame.events.onDelete : undefined,
    };

    return (
        <EditFrameShell
            frame={frame}
            headerProps={{
                label: order.type === EnumOrderType.Cash ? 'Barauftrag' : 'Auftrag',
                resourceLabel: 'Auftrag',
                ...frameEvents,
                actionItems: orderActionItems(actions, i18n),
                menuItems: orderMenuItems(actions, canEdit),
            }}
            bannerProps={{
                mode: frame.mode,
                loading: frame.pendingAction !== undefined,
                ...frameEvents,
            }}
        >
            <OrderSidePanel order={order} containers={containers} openOrder={openOrder} correction={siteCorrection} />

            <OrderMainSection
                binder={binder}
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={frame.requestUpdateState}
                canEdit={canEdit}
                stateBadges={stateBadges}
                orderType={order.type}
                taxationMode={order.taxation_mode}
                totalNetPrice={totalNetPrice}
                totalGrossPrice={totalGrossPrice}
                siteAddress={order.site.address}
                customerKey={order.customer.foreign_key}
                pickupSiteKey={order.pickup_site.foreign_key}
                hasContainerLines={order.container_actions.length > 0}
                containersBooked={order.container_actions.some((action) => isBooked(action.booking_status))}
            />

            <OrderDispositionSection
                binder={binder}
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={frame.requestUpdateState}
            />

            <OrderContainersSection
                data={order}
                apiResourcePath={apiResourcePath}
                canEdit={canEditContainers}
                requestUpdateState={frame.requestUpdateState}
                refetchOrder={frame.refetchData}
                planning={containers.planning}
                standingCount={containers.standingCount}
                site={containers.knownSite}
                offersPlacement={!embedded && acceptsNewContainerActions(order.process_state, canEdit)}
                atEditStart={atEditStart}
            />

            <OrderPositionsSection
                baseApiObjectPath={baseApiObjectPath}
                requestUpdateState={frame.requestUpdateState}
                stateQueueDepth={frame.stateQueueDepth}
                refetchData={frame.refetchData}
                positions={order.positions}
                containerActions={order.container_actions}
                taxationMode={order.taxation_mode}
                processState={order.process_state}
                totalNetPrice={totalNetPrice}
                canEdit={canEdit}
                canEditPositions={canEdit && !forceReadOnly('positions')}
            />

            <OrderRemarksSection binder={binder} order={order} canEdit={canEdit} />

            <OrderCollectionModal
                actions={actions}
                order={order}
                totalGrossPrice={totalGrossPrice}
                requestUpdateState={frame.requestUpdateState}
                refetchData={frame.refetchData}
            />
            <OrderOutputModals actions={actions} orderId={order.id} />
        </EditFrameShell>
    );
}
